/**
 * The wrap-up of a finished plan at the project's home (AIOE 0.65.0). The workbench works the wrap-up out on the
 * desktop (`PlanState.wrapUp`, its `shared/protocol/wrapUp.ts`, mirrored here as `@aioe/shared/models`) and
 * publishes it here, so the console, the phone and every member's workbench see where a plan's wrap-up stands: who
 * can write the outcome report, whether it is being written, the criteria checks, the open follow-up tasks, how
 * much of the change has been read. A person answers a step from any screen; what only an agent or the desktop can
 * do (write the report, run the checks, move a task, close the plan) is kept as an ask the workbench picks up from
 * the record and says it handled. AIOE's own contract; the workbench half publishes and handles.
 *
 * It is a project record of kind `plan-wrap-up:<rootId>` with the plan's id, so it rides the board stream (a
 * `record` event) and the record routes' versions:
 *
 *   PUT    /projects/v1/:id/roots/:rootId/plans/:planId/wrap-up   contributors and above, a sign-in or their desktop:
 *          {@link PlanWrapUpPut} (`wrapUp`, `handled[]`) -> {@link PlanWrapUpRecord}; If-Match optional (the
 *          record's version); 404 unknown plan; asks it names in `handled` are dropped, the rest kept; a pending
 *          `waive` ask is applied over the published state until it is handled
 *   GET    …/wrap-up      members: {@link PlanWrapUpRecord}; 404 when the plan has none (its work is not finished,
 *                         or it is closed)
 *   DELETE …/wrap-up      contributors: the plan is closed or its work reopened; 204
 *   POST   …/wrap-up/asks contributors: {@link PlanWrapUpAskInput} -> 201 {@link PlanWrapUpRecord}; 409 when the
 *                         plan has no wrap-up; at most {@link WRAP_UP_ASKS_MAX} waiting (the oldest goes)
 *   GET    /projects/v1/:id/wrap-ups?rootId=   members: {@link PlanWrapUpListResponse}, every open wrap-up
 *
 * Each route also stands at the project's own path (`/projects/v1/:id/plans/:planId/wrap-up`, root ""), as the plan
 * routes do. Criteria verdicts are the plan's own (`POST …/plans/:planId/acceptance`), not an ask.
 *
 * Words for people: wrap-up, outcome report, criteria, follow-up tasks, review, close the plan.
 */
import { z } from "zod";
export const PLAN_WRAP_UP_SCHEMA_VERSION = 1;
/** The record kind, per repository ("" for the project's own plans). */
export const planWrapUpKind = (rootId) => `plan-wrap-up:${rootId}`;
export const WRAP_UP_ASKS_MAX = 20;
const name = z.string().min(1).max(200);
export const wrapUpWaivableSchema = z.enum(["report", "review"]);
/** `PlanWrapUpHost` (the workbench's protocol/wrapUp.ts), checked. */
export const planWrapUpHostSchema = z.object({
    manager: z.object({ name, running: z.boolean() }).optional(),
    writers: z.array(name).max(50).default([]),
    scribe: name.optional(),
    running: z.object({ by: name }).optional(),
    reportAt: z.string().max(50).optional(),
    verifier: name.optional(),
    verifying: z.boolean().optional(),
    waived: z.array(wrapUpWaivableSchema).max(2).default([]),
    followUps: z
        .array(z.object({ id: z.string().min(1).max(100), title: z.string().max(500), status: z.string().max(40), assignee: z.string().max(200).optional(), movable: z.boolean() }))
        .max(200)
        .default([]),
    review: z.object({ files: z.number().int().nonnegative(), reviewed: z.number().int().nonnegative() }).optional(),
});
export const planWrapUpPutSchema = z.object({
    wrapUp: planWrapUpHostSchema,
    /** Asks the workbench acted on (by id): they leave the record. */
    handled: z.array(z.string().max(100)).max(WRAP_UP_ASKS_MAX).optional(),
});
/** What a person can ask of a wrap-up from any screen. */
export const planWrapUpAskInputSchema = z.discriminatedUnion("action", [
    /** Write the outcome report: by the manager, or the named stand-in. */
    z.object({ action: z.literal("start-report"), by: name.optional() }),
    /** Do without the report or the review (or, `waived: false`, take it back). */
    z.object({ action: z.literal("waive"), step: wrapUpWaivableSchema, waived: z.boolean() }),
    /** Run the criteria checks. */
    z.object({ action: z.literal("verify") }),
    /** Settle a follow-up task: to the backlog, or not needed. */
    z.object({ action: z.literal("follow-up"), taskId: z.string().min(1).max(100), move: z.enum(["backlog", "not-needed"]) }),
    /** Close the plan; with a reason when steps are not done ("close it anyway"). */
    z.object({ action: z.literal("close"), reason: z.string().min(1).max(2000).optional() }),
]);
/** The words for an ask that waits on the workbench. */
export function wrapUpAskWords(a) {
    switch (a.action) {
        case "start-report":
            return a.by ? `Asked ${a.by} to write the outcome report` : "Asked for the outcome report";
        case "waive":
            return a.waived ? (a.step === "report" ? "Asked to close without a report" : "Asked to skip the review") : a.step === "report" ? "Asked for the report after all" : "Asked to review it after all";
        case "verify":
            return "Asked for the criteria to be checked";
        case "follow-up":
            return a.move === "backlog" ? `Asked to move ${a.taskId} to the backlog` : `Asked to close ${a.taskId} as not needed`;
        case "close":
            return a.reason ? "Asked to close the plan anyway" : "Asked to close the plan";
    }
}
/** The published state with the pending waive asks applied over it, newest last: what every screen shows. */
export function wrapUpAsShown(wrapUp, asks) {
    const waived = new Set(wrapUp.waived);
    for (const a of asks) {
        if (a.action !== "waive")
            continue;
        if (a.waived)
            waived.add(a.step);
        else
            waived.delete(a.step);
    }
    return { ...wrapUp, waived: [...waived] };
}
//# sourceMappingURL=planWrapUp.js.map